// SMS segment counting, GSM 03.38 exact. Browser-safe, no dependencies.
import type { SegmentInfo } from './types';

// The tables are written as \u escapes on purpose so the file stays pure ASCII: an invisible
// control character or an NFD re-normalisation in an editor cannot silently corrupt the set.

/** GSM 03.38 basic character set (128 code points, one septet each). Includes ESC (0x1B). */
const GSM_BASIC =
  // 0x00-0x0F: @ pound $ yen e-grave e-acute u-grave i-grave o-grave C-cedilla LF O-stroke o-stroke CR A-ring a-ring
  '@\u00A3$\u00A5\u00E8\u00E9\u00F9\u00EC\u00F2\u00C7\n\u00D8\u00F8\r\u00C5\u00E5' +
  // 0x10-0x1F: Delta _ Phi Gamma Lambda Omega Pi Psi Sigma Theta Xi ESC AE ae sharp-s E-acute
  '\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039E\u001B\u00C6\u00E6\u00DF\u00C9' +
  // 0x20-0x3F: space ! " # currency % & ' ( ) * + , - . / digits : ; < = > ?
  ' !"#\u00A4%&\'()*+,-./0123456789:;<=>?' +
  // 0x40-0x5F: inverted-! A-Z A-umlaut O-umlaut N-tilde U-umlaut section
  '\u00A1ABCDEFGHIJKLMNOPQRSTUVWXYZ\u00C4\u00D6\u00D1\u00DC\u00A7' +
  // 0x60-0x7F: inverted-? a-z a-umlaut o-umlaut n-tilde u-umlaut a-grave
  '\u00BFabcdefghijklmnopqrstuvwxyz\u00E4\u00F6\u00F1\u00FC\u00E0';

/** GSM 03.38 extension table, two septets each (ESC + char): ^ { } backslash [ ~ ] | euro form-feed. */
const GSM_EXT = '^{}\\[~]|\u20AC\f';

const BASIC = new Set(Array.from(GSM_BASIC));
const EXT = new Set(Array.from(GSM_EXT));

export const GSM7_SINGLE = 160;
export const GSM7_MULTI = 153;
export const UCS2_SINGLE = 70;
export const UCS2_MULTI = 67;

/** Max number of distinct UCS-2 trigger characters reported. */
const MAX_TRIGGERS = 3;

/** true when the character is representable in GSM-7 (basic or extension table). */
export function isGsm7Char(ch: string): boolean {
  return BASIC.has(ch) || EXT.has(ch);
}

export function countSegments(body: string): SegmentInfo {
  if (body.length === 0) {
    return { encoding: 'gsm7', length: 0, segments: 0, capacity: GSM7_SINGLE, remaining: GSM7_SINGLE, ucs2Triggers: [] };
  }

  let septets = 0;
  let gsm = true;
  const triggers: string[] = [];
  const seen = new Set<string>();

  for (const ch of Array.from(body)) {
    if (BASIC.has(ch)) {
      septets += 1;
    } else if (EXT.has(ch)) {
      septets += 2;
    } else {
      gsm = false;
      if (!seen.has(ch)) {
        seen.add(ch);
        if (triggers.length < MAX_TRIGGERS) triggers.push(ch);
      }
    }
  }

  if (gsm) {
    const segments = septets <= GSM7_SINGLE ? 1 : Math.ceil(septets / GSM7_MULTI);
    const capacity = segments === 1 ? GSM7_SINGLE : segments * GSM7_MULTI;
    return { encoding: 'gsm7', length: septets, segments, capacity, remaining: capacity - septets, ucs2Triggers: [] };
  }

  // UCS-2: length is the UTF-16 code-unit count (an astral emoji is 2).
  const length = body.length;
  const segments = length <= UCS2_SINGLE ? 1 : Math.ceil(length / UCS2_MULTI);
  const capacity = segments === 1 ? UCS2_SINGLE : segments * UCS2_MULTI;
  return { encoding: 'ucs2', length, segments, capacity, remaining: capacity - length, ucs2Triggers: triggers };
}

// ---- plain words for UCS-2 triggers, and the one-click fix (review round 1, trap 5) ----------
// Written as \u escapes like the tables above: a literal U+2028 inside a regex is a syntax error.

const NBSP = '\u00A0';
const NNBSP = '\u202F';
const FIGURE_SPACE = '\u2007';
const ZWSP = '\u200B';
const ZWNJ = '\u200C';
const ZWJ = '\u200D';
const BOM = '\uFEFF';
const WORD_JOINER = '\u2060';
const LINE_SEP = '\u2028';
const PARA_SEP = '\u2029';
const TAB = '\u0009';
const VT = '\u000B';
const SOFT_HYPHEN = '\u00AD';
const THIN_SPACE = '\u2009';
const MED_SPACE = '\u205F';
const IDEO_SPACE = '\u3000';

/** Characters the eye cannot see (or mistakes for a plain one), with the words the composer shows. */
const TRIGGER_NAMES: Record<string, string> = {
  [NBSP]: 'a hidden no-break space',
  [NNBSP]: 'a hidden no-break space',
  [FIGURE_SPACE]: 'a hidden no-break space',
  [ZWSP]: 'a hidden zero-width character',
  [ZWNJ]: 'a hidden zero-width character',
  [ZWJ]: 'a hidden zero-width character',
  [BOM]: 'a hidden zero-width character',
  [WORD_JOINER]: 'a hidden zero-width character',
  [LINE_SEP]: 'a hidden line separator',
  [PARA_SEP]: 'a hidden line separator',
  // review round 2: these arrive by pasting a spreadsheet row, a web page or PDF text. They all
  // force 70-character segments and the old message said "the character " with nothing after it.
  [TAB]: 'a hidden tab',
  [VT]: 'a hidden line break',
  [SOFT_HYPHEN]: 'a hidden soft hyphen',
  [THIN_SPACE]: 'a hidden thin space',
  [MED_SPACE]: 'a hidden thin space',
  [IDEO_SPACE]: 'a hidden wide space',
  '\u2002': 'a hidden thin space',
  '\u2003': 'a hidden thin space',
  '\u2004': 'a hidden thin space',
  '\u2005': 'a hidden thin space',
  '\u2006': 'a hidden thin space',
  '\u2008': 'a hidden thin space',
  '\u200A': 'a hidden thin space',
  '\u0301': 'an accent mark on its own',
  '\u0300': 'an accent mark on its own',
  '\u0303': 'an accent mark on its own',
  '\u2018': 'a curly quote \u2018',
  '\u2019': 'a curly apostrophe \u2019',
  '\u201C': 'a curly quote \u201C',
  '\u201D': 'a curly quote \u201D',
  '\u2013': 'a dash \u2013',
  '\u2014': 'a dash \u2014',
  '\u2026': 'an ellipsis character \u2026',
};

const INVISIBLE_RE = /^[\u0009\u000B\u00A0\u00AD\u202F\u2007\u200B\u200C\u200D\uFEFF\u2060\u2028\u2029\u2002-\u200A\u205F\u3000\u0300\u0301\u0303]$/;

/** true when the character is one nobody can see on the screen */
export function isInvisibleChar(ch: string): boolean {
  return INVISIBLE_RE.test(ch);
}

/** The word before `index` in `body`, for 'after "Call"' hints; null at the start. */
function wordBefore(body: string, index: number): string | null {
  const before = body.slice(0, index).trimEnd();
  const m = /(\S+)$/.exec(before);
  return m ? m[1] : null;
}

/**
 * One plain sentence naming what forced UCS-2, so the operator can find and delete it.
 * Visible triggers are shown as they are; invisible ones get a name and where they sit
 * ('a hidden no-break space after "Call"').
 */
export function describeUcs2Triggers(body: string, triggers: string[] = countSegments(body).ucs2Triggers): string {
  if (triggers.length === 0) return '';
  const parts: string[] = [];
  for (const ch of triggers) {
    const named = TRIGGER_NAMES[ch];
    if (named) {
      const at = body.indexOf(ch);
      const word = at > 0 ? wordBefore(body, at) : null;
      parts.push(word ? `${named} after "${word}"` : named);
    } else if (/\p{Extended_Pictographic}/u.test(ch)) {
      parts.push(`the emoji ${ch}`);
    } else {
      parts.push(`the character ${ch}`);
    }
  }
  const first = triggers[0];
  if (TRIGGER_NAMES[first]) return parts.join(', ') + ' switched this to 70-character segments';
  const lead = /\p{Extended_Pictographic}/u.test(first) ? 'an emoji' : 'a special character';
  return `${lead} switched this to 70-character segments: ${parts.join(', ')}`;
}

/**
 * "Make plain text": the smart punctuation and hidden characters that word processors and
 * phones slip in become their GSM-7 cousins. Emoji and real accented letters are left alone.
 */
export function plainText(body: string): string {
  return (
    body
      // NFC first: text pulled out of a PDF carries 'e' + a combining acute, which is two code
      // points and forces UCS-2; composed 'e-acute' is a plain GSM-7 letter (review round 2).
      .normalize('NFC')
      .replace(/[\u0009\u2002-\u200A\u205F\u3000]/g, ' ')
      .replace(/\u000B/g, '\n')
      .replace(/\u00AD/g, '')
  )
    .replace(/[\u00A0\u202F\u2007]/g, ' ')
    .replace(/[\u200B\u200C\u200D\uFEFF\u2060]/g, '')
    .replace(/[\u2028\u2029]/g, '\n')
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u00B4]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g, '-')
    .replace(/\u2026/g, '...');
}
